import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { TOURS } from './tour-definitions';

interface TourState {
    key: string;
    step: number;
}

interface TourContextValue {
    tour: TourState | null;
    startTour: (key: string) => void;
    nextStep: () => void;
    stopTour: () => void;
}

const TourContext = createContext<TourContextValue>({
    tour: null,
    startTour: () => {},
    nextStep: () => {},
    stopTour: () => {},
});

export const useTour = () => useContext(TourContext);

// Ephemeral by design: tours restart from step 1 every time — nothing persisted.
export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [tour, setTour] = useState<TourState | null>(null);

    const startTour = useCallback((key: string) => {
        if (!TOURS[key]) return;
        setTour({ key, step: 0 });
    }, []);

    const nextStep = useCallback(() => {
        setTour(prev => {
            if (!prev) return null;
            const steps = TOURS[prev.key];
            return prev.step + 1 < steps.length ? { ...prev, step: prev.step + 1 } : null;
        });
    }, []);

    const stopTour = useCallback(() => setTour(null), []);

    const value = useMemo(() => ({ tour, startTour, nextStep, stopTour }), [tour, startTour, nextStep, stopTour]);
    return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};
